<template>
  <div class="rowSpaceBetween fullWidth my-5">
    <TopDeck firstElement="Santé" />
  </div>

  <div class="healthContainer">
    <div class="healthLeft">

      <!-- Répartition des macronutriments -->
      <div class="healthBloc">
        <h3 class="uppercase">Apports journaliers recommandés</h3>
        <p>
          <strong>Calories visées :</strong> {{ targetCalories.toFixed(0) }} kcal / jour
        </p>

        <div class="macros">
          <div class="macro protein">
            <p>Protéines</p>
            <strong>{{ proteinNeeds.toFixed(0) }} g</strong>
          </div>
          <div class="macro fat">
            <p>Lipides</p>
            <strong>{{ fatNeeds.toFixed(0) }} g</strong>
          </div>
          <div class="macro carb">
            <p>Glucides</p>
            <strong>{{ carbNeeds.toFixed(0) }} g</strong>
          </div>
          <div class="macro fibre">
            <p>Fibres</p>
            <strong>30 g</strong>
          </div>
        </div>
      </div>

      <!-- IMC -->
      <div class="healthBloc">
        <h3 class="uppercase">Indice de Masse Corporelle (IMC)</h3>

        <div class="imcBar">
          <div class="zone under"></div>
          <div class="zone normal"></div>
          <div class="zone over"></div>
          <div class="zone obese"></div>
          <div class="zone extreme"></div>
          <div class="indicator" :style="{ left: bmiPercent + '%' }"></div>
        </div>

        <p class="mt-3">
          <strong>Votre IMC :</strong> {{ bmi.toFixed(1) }} – {{ bmiStatus }}
        </p>
      </div>
    </div>

    <div class="healthRight">
      <div class="healthBloc">
        <h3 class="uppercase">Dépenses énergétiques</h3>
        <p><strong>Taux métabolique de base :</strong> {{ tmb.toFixed(0) }} kcal / jour</p>
        <p><strong>Niveau d’activité :</strong> {{ $t(activityLevel) }}</p>
        <p><strong>Besoins journaliers :</strong> {{ dailyNeeds.toFixed(0) }} kcal / jour</p>
        <p><strong>Objectif :</strong> {{ $t(weightObjective) }}</p>
        <p><strong>Ajustement objectif :</strong> {{ calorieAdjustmentLabel }}</p>
        <p v-if="estimatedMonthsLabel"><strong>Temps estimé :</strong> {{ estimatedMonthsLabel }}</p>
        <p><strong>Poids actuel :</strong> {{ formatWeight(actualWeight) }}</p>
        <p><strong>Poids visé :</strong> {{ formatWeight(displayTargetWeight) }}</p>

        <div class="resultCards">
          <div class="card neutral">
            <div class="calorie-line">
              <span class="calDisplay">{{ tmb.toFixed(0) }}</span> 
              <p>kcal</p>
            </div>
            <p>Taux métabolique de base</p>
          </div>
          <div class="card target">
            <div class="calorie-line">
              <span class="calDisplay">{{ targetCalories.toFixed(0) }}</span>
              <p>kcal</p>
            </div>
            <p>Calories visées</p>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { computed } from "vue";
import TopDeck from "../Utils/TopDeck.vue";
import { useAuthStore } from "@/store/auth_store";
import {
  getBaseMetabolicRate,
  getDailyNeeds,
  getTargetCalories,
  calculateBmiData,
  calculateMacros
} from "@/service/health_service";

export default {
  name: "HealthPage",
  components: { TopDeck },
  setup() {
    const authStore = useAuthStore();

    const toNumber = (value) => {
      const num = Number(value);
      return Number.isFinite(num) ? num : null;
    };

    const actualWeight = computed(() =>
      toNumber(authStore.userInfos?.actualWeight)
    );
    const targetWeight = computed(() =>
      toNumber(authStore.userInfos?.targetWeight)
    );
    const displayTargetWeight = computed(() =>
      targetWeight.value ?? actualWeight.value
    );

    const weight = computed(() => toNumber(authStore.userInfos?.actualWeight) ?? 70);
    const height = computed(() => toNumber(authStore.userInfos?.actualSize) ?? 175);
    const age = computed(() => authStore.userInfos?.age || 30);
    const sex = computed(() => authStore.userInfos?.sex || "male");
    const activityLevel = computed(() => authStore.userInfos?.activityLevel || "sedentary");
    const weightObjective = computed(() => authStore.userInfos?.weightObjective || "maintainWeight");
    const nap = computed(() => authStore.userInfos?.NAP || 1.5);

    const bmiData = computed(() => calculateBmiData(height.value, weight.value));
    const bmi = computed(() => bmiData.value.bmi);
    const bmiStatus = computed(() => bmiData.value.status);
    const bmiPercent = computed(() => bmiData.value.percent);

    const tmb = computed(() => getBaseMetabolicRate(age.value, height.value, weight.value, sex.value));
    const dailyNeeds = computed(() => getDailyNeeds(tmb.value, nap.value));
    const targetCalories = computed(() => getTargetCalories(dailyNeeds.value, weightObjective.value));

    const macros = computed(() =>
      calculateMacros(weight.value, activityLevel.value, weightObjective.value, targetCalories.value)
    );

    const proteinNeeds = computed(() => macros.value.proteinNeeds);
    const fatNeeds = computed(() => macros.value.fatNeeds);
    const carbNeeds = computed(() => macros.value.carbNeeds);

    const calorieAdjustment = computed(() => {
      if (weightObjective.value === "gainWeight") return 380;
      if (weightObjective.value === "loseWeight") return -380;
      return 0;
    });

    const calorieAdjustmentLabel = computed(() => {
      if (calorieAdjustment.value === 0) return "0 kcal (maintien)";
      const sign = calorieAdjustment.value > 0 ? "+" : "";
      return `${sign}${calorieAdjustment.value} kcal (≈ 1,5 kg/mois)`;
    });

    const estimatedMonthsLabel = computed(() => {
      if (weightObjective.value === "maintainWeight") return "";
      if (actualWeight.value == null || targetWeight.value == null) return "";
      const delta = Math.abs(targetWeight.value - actualWeight.value);
      if (delta === 0) return "Objectif atteint.";
      const months = delta / 1.5;
      return `≈ ${months.toFixed(1).replace(".", ",")} mois`;
    });

    const formatWeight = (value) => {
      if (value == null) return "--";
      return `${value.toFixed(1)} kg`;
    };

    return {
      authStore,
      bmi,
      bmiStatus,
      bmiPercent,
      tmb,
      activityLevel,
      weightObjective,
      dailyNeeds,
      targetCalories,
      proteinNeeds,
      fatNeeds,
      carbNeeds,
      actualWeight,
      displayTargetWeight,
      calorieAdjustmentLabel,
      estimatedMonthsLabel,
      formatWeight,
    };
  },
};
</script>

<style scoped>
  .healthContainer {
    display: flex;
    flex-direction: column;
    gap: 1.5rem;
  }

  .healthLeft,
  .healthRight {
    flex: 1;
    display: flex;
    flex-direction: column;
    gap: 1.5rem;
  }

  .healthBloc {
    flex: 1;
    background-color: rgb(var(--v-theme-secondary));
    padding: 1.5em;
    border-radius: 1em;
  }

  /* --- IMC --- */
  .imcBar {
    position: relative;
    display: flex;
    height: 20px;
    border-radius: 10px;
    overflow: hidden;
    margin-top: 1rem;
  }

  .zone {
    height: 100%;
    display: inline-block;
  }

  .under {
    background-color: #DD0001;
    width: 37%;
  }
  .normal {
    background-color: #42B129;
    width: 13%;
  }
  .over {
    background-color: #ffda0a;
    width: 10%;
  }
  .obese {
    background-color: #fc7f19;
    width: 10%;
  }
  .extreme {
    background-color: #DD0001;
    width: 30%;
  }

  .indicator {
    position: absolute;
    top: -5px;
    width: 5px;
    height: 30px;
    background: white;
    border-radius: 2px;
    border: 1px solid rgba(0, 0, 0, 0.404);
  }

  /* --- Résultats & macros --- */
  .resultCards {
    display: flex;
    gap: 1rem;
    margin-top: 1.5rem;
  }

  .card {
    flex: 1;
    padding: 1rem;
    border-radius: 0.8rem;
    text-align: start;
  }

  .card.neutral {
    background: rgb(var(--v-theme-tertiary));
    color: rgb(var(--v-theme-text));
  }

  .card.target {
    background: rgb(var(--v-theme-accent));
    color: white;
    font-weight: bold;
  }

  .calDisplay {
    font-size: 2.25rem;
    line-height: 1.1;
    font-weight: bold;
    display: block;
  }

  .calorie-line {
    display: flex;
    align-items: baseline;
    justify-content: flex-start;
    gap: 4px;
  }

  /* --- Macros --- */
  .macros {
    display: flex;
    justify-content: space-around;
    margin-top: 1rem;
  }

  .macro {
    text-align: center;
  }

  .macro.protein p {
    color: #3b82f6;
  }
  .macro.fat p {
    color: #f59e0b;
  }
  .macro.carb p {
    color: #10b981;
  }
  .macro.fibre p {
    color: #a78bfa;
  }

  .uppercase {
    text-transform: uppercase;
  }

  /* --- Responsive --- */
  @media (min-width: 900px) {
    .healthContainer {
      flex-direction: row;
      align-items: stretch;
    }

    .healthLeft,
    .healthRight {
      flex: 1;
    }
  }
</style>
